// React Component
import React, { useEffect, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"

// App Component
import { useAppSelector } from "../stores"
import Spiner from '../components/Spiner'
import Dropdown from '../components/Dropdown'
import SidebarUserProfileSection from '../components/SidebarUserProfileSection'

// utils
import { slice } from "../utils"
import { FamlaServices } from "../services"
import { Routes } from "../routes"
import { navigateToMapping } from "../utils/mappingAccess"
import { resolveCollectCollectionOpenPath } from "../services/api/collectDataService"
import {
    ANALYSIS_NAME_EXISTS_ERROR,
    CompanyAnalysisService,
} from "../services/api/companyAnalysisService"

// interface
import { Report } from "../swimlane/lib/types"
import { iCompany, iMap } from "../components/type"
import type { SelectedCollectCollection } from "../utils/analysisContextLinks"
import type { InsightsInaccessibleCollectionLink } from "../services/api/collectDataService"

import { AnalysisState } from "../views/analysis/hooks/useCompanyAnalysis"
import { InsufficientCreditsModal } from "../views/modal"
import { useTheme } from "../contexts/ThemeContext"
import "../views/analysis/company-analysis-page.css"
import "./header-redesign.css"
import SearchInput from "../components/form/SearchInput"

function isAnalysisOwner(
    analysis: { user_id?: string | null },
    currentUserId?: string | null,
): boolean {
    const ownerId = analysis?.user_id;
    if (!ownerId || !currentUserId) return false;
    return String(ownerId) === String(currentUserId);
}

interface CompanyAnalysisSidebarProps {
    onSelectProcess?: (processId: string) => void
    onSelectAnalysis?: (analysisId: string, analysisName?: string) => Promise<boolean>
    onNewDocument?: () => void
    onNewAnalysis?: () => void
    onDeleteAnalysis?: (analysisId: string) => void // Callback après suppression
    selectedFile?: File | null // Compatibilité rétroactive
    selectedFiles?: File[] // Nouveau format pour plusieurs fichiers
    selectedMaps?: iMap[]
    selectedCollections?: SelectedCollectCollection[]
    onRemoveFile?: (fileIndex?: number) => void
    onRemoveMap?: (mapId: string) => void
    onRemoveCollection?: (collectionId: string) => void
    inaccessibleLinks?: InsightsInaccessibleCollectionLink[]
    analysisState?: AnalysisState
    lastSaved?: number // Timestamp pour forcer rechargement après sauvegarde
    currentAnalysisId?: string // ID de l'analyse actuellement chargée
    onSearch?: (query: string) => void // Connecte la recherche sidebar au moteur de recherche global
}

const CompanyAnalysisSidebar: React.FC<CompanyAnalysisSidebarProps> = ({
    onSelectProcess,
    onSelectAnalysis,
    onNewDocument,
    onNewAnalysis,
    onDeleteAnalysis,
    selectedFile, // Compatibilité rétroactive
    selectedFiles, // Nouveau format
    selectedMaps,
    selectedCollections,
    onRemoveFile,
    onRemoveMap,
    onRemoveCollection,
    inaccessibleLinks = [],
    analysisState = 'idle',
    lastSaved,
    currentAnalysisId,
    onSearch,
}) => {
    const { t } = useTranslation()
    const { theme } = useTheme()
    const modalCloseClass = theme === 'light' ? 'btn-close' : 'btn-close-white'
    const users = useAppSelector(state => state.auth)
    const role = useAppSelector(state => state.role.role)
    const company = useAppSelector((state) => state.company.company)
    const companyId = users.session?.company_id;
    const [companyById, setCompanyById] = React.useState<iCompany | null>(null);
    // Déterminer si l'utilisateur actuel est le propriétaire de la compagnie ou un admin
    const isCompanyOwner = Boolean(
        company?.owner?._id && 
        users?.user?._id && 
        (company.owner._id === users.user._id || 
         company.owner._id?.toString() === users.user._id?.toString())
    );
    const isAdmin = role === "ADMIN" || role === "admin";
    const isOwner = isCompanyOwner || isAdmin;
    // Récupérer l'email du propriétaire de la company pour les utilisateurs non-admin
    const [ownerEmail, setOwnerEmail] = React.useState<string | undefined>(undefined);
    
    React.useEffect(() => {
        if (!companyId || !users.access_token) return;
        let cancelled = false;
        void (async () => {
            try {
                const data = await FamlaServices("api_host", `/company/by/id/${companyId}`, "GET", "", users.access_token);
                if (!cancelled && data.status === 200 && data.body?.data) {
                    setCompanyById(data.body.data);
                    if (!isAdmin && data.body.data?.owner?.email) {
                        setOwnerEmail(data.body.data.owner.email);
                    }
                }
            } catch {
                /* silent */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId, users.access_token, isAdmin]);

    const [analyses, setAnalyses] = useState<Report[]>([])
    const [isLoadingAnalyses, setIsLoadingAnalyses] = useState(false)

    // Recherche dans la sidebar (filtre processus & insights)
    const [sidebarSearch, setSidebarSearch] = useState('')

    // Show/Hide pour la section Recent Insights
    const [insightsVisible, setInsightsVisible] = useState(true)

    // États pour les modals et actions
    const [selectedAnalysisId, setSelectedAnalysisId] = useState<string>("")
    const [selectedAnalysisName, setSelectedAnalysisName] = useState<string>("")
    const [modalAction, setModalAction] = useState<'delete' | 'rename' | 'share' | null>(null)
    const [renameError, setRenameError] = useState('')
    const [isRenaming, setIsRenaming] = useState(false)
    
    // État pour l'analyse actuellement active
    const [activeAnalysisId, setActiveAnalysisId] = useState<string>("")
    
    // Ref pour éviter les double appels
    const isLoadingRef = React.useRef(false)
    
    // État pour le modal de crédits insuffisants
    const [insufficientCreditsInfo, setInsufficientCreditsInfo] = useState<{
        creditsRequired: number;
        creditsAvailable: number;
        deficit: number;
        plan?: string;
    } | null>(null)
    
    // Ref pour le modal
    const insufficientCreditsModalRef = useRef<HTMLSpanElement>(null)


    const loadRecentAnalyses = React.useCallback(async () => {
        // Protection contre les appels multiples simultanés
        if (isLoadingRef.current) return
        
        isLoadingRef.current = true
        setIsLoadingAnalyses(true)
        try {
            const response = await FamlaServices(
                "api_host",
                `/report/saved/analyses/${users.session.company_id}`,
                "GET",
                "",
                users.access_token
            )
            if (response.status === 200) {
                const data = response.body.data;
                if (Array.isArray(data) && data.length > 0) {
                    setAnalyses(data)
                } else {
                    setAnalyses([])
                }
            }
        } catch (error) {
            console.error("Erreur lors du chargement des analyses:", error)
            setAnalyses([])
        } finally {
            setIsLoadingAnalyses(false)
            // Permettre un nouvel appel après un court délai
            setTimeout(() => {
                isLoadingRef.current = false
            }, 300)
        }
    }, [users.session.company_id, users.access_token])

    // Charger / recharger quand la compagnie active change
    useEffect(() => {
        loadRecentAnalyses();
    }, [loadRecentAnalyses]);

    // Recharger « Insights récents » après chaque sauvegarde (lastSaved = timestamp).
    // Ne pas exiger analysisState === 'saved' : au moment du save on passe souvent
    // draft → idle via resetAnalysis(), donc la condition bloquait le refresh.
    useEffect(() => {
        if (!lastSaved || lastSaved <= 0) return

        const timer = setTimeout(() => {
            loadRecentAnalyses()
        }, 500)

        return () => clearTimeout(timer)
    }, [lastSaved, loadRecentAnalyses])

    // Mettre à jour l'analyse active quand elle est chargée depuis l'URL (processView)
    useEffect(() => {
        if (currentAnalysisId) {
            setActiveAnalysisId(currentAnalysisId)
        }
    }, [currentAnalysisId])


    /**
     * Supprime une analyse
     * Note: Supprime l'objet Analysis ET tous les Reports associés
     */
    const deleteAnalysis = async (id: string, actionName: string, secondAction: string) => {
        // Mise à jour optimiste (retirer de la liste immédiatement)
        setAnalyses((prev) => prev.filter((a: any) => (a.id || a._id) !== id))
        
        try {
            // Supprimer l'analyse complète (l'endpoint supprime tous les reports + l'objet Analysis)
            await FamlaServices(
                "api_host",
                `/report/analysis/${id}`,
                "DELETE",
                "",
                users.access_token
            )
            
            // ✅ Si l'analyse supprimée est celle actuellement affichée, réinitialiser l'écran
            if (onDeleteAnalysis && currentAnalysisId === id) {
                onDeleteAnalysis(id)
            }
            
            // Recharger pour être sûr
            setTimeout(() => loadRecentAnalyses(), 500)
        } catch (error) {
            console.error("Erreur lors de la suppression:", error)
            // Recharger en cas d'erreur
            loadRecentAnalyses()
        }
    }

    /**
     * Prépare le renommage d'une analyse
     * Ouvre un modal pour saisir le nouveau nom
     */
    const renameAnalysis = async (id: string, name: string, secondAction: string) => {
        setSelectedAnalysisId(id)
        setSelectedAnalysisName(name)
        setRenameError('')
        setModalAction('rename')
    }

    /**
     * Confirme le renommage avec le nouveau nom
     */
    const confirmRenameAnalysis = async (newName: string) => {
        if (!selectedAnalysisId || !newName.trim()) return

        setIsRenaming(true)
        setRenameError('')

        try {
            await CompanyAnalysisService.renameAnalysis(
                selectedAnalysisId,
                newName.trim(),
                users.access_token,
            )

            setTimeout(() => loadRecentAnalyses(), 500)
            setModalAction(null)
            setSelectedAnalysisId("")
            setSelectedAnalysisName("")
            setRenameError('')
        } catch (error: any) {
            const message = error?.message === ANALYSIS_NAME_EXISTS_ERROR
                ? (t('features.swimlane.analysisNameAlreadyExists') || 'Une analyse avec ce nom existe déjà dans votre entreprise.')
                : (error?.message || t('features.swimlane.errorSavingAnalysis') || 'Erreur lors du renommage')
            setRenameError(message)
            console.error("Erreur lors du renommage:", error)
        } finally {
            setIsRenaming(false)
        }
    }

    /**
     * Prépare le partage d'une analyse
     * Ouvre un modal avec le lien de partage
     */
    const shareAnalysis = async (analysisId: string) => {
        setSelectedAnalysisId(analysisId)
        setModalAction('share')
    }

    // Utiliser selectedFiles si disponible, sinon selectedFile (compatibilité)
    const filesToDisplay = React.useMemo(() => {
        if (selectedFiles && selectedFiles.length > 0) {
            return selectedFiles;
        }
        if (selectedFile) {
            return [selectedFile];
        }
        return [];
    }, [selectedFiles, selectedFile]);

    const handleCollectionClick = async (collectionId: string) => {
        if (!collectionId || !users.access_token) return;
        const path = await resolveCollectCollectionOpenPath(collectionId, users.access_token, {
            id: users.user?._id,
            email: users.user?.email,
        });
        if (path) {
            window.open(path, '_blank', 'noopener,noreferrer');
        }
    };

    /**
     * Gère le clic sur une cartographie sélectionnée
     * Vérifie les crédits avant d'ouvrir le mapping
     * Même logique que ProcessInterview.tsx pour cohérence
     */
    const handleMapClick = async (e: React.MouseEvent, mapId: string, processId: string) => {
        e.preventDefault();
        e.stopPropagation();

        const result = await navigateToMapping({
            processId,
            mapId,
            accessToken: users.access_token || '',
            newTab: true,
        });
        if (!result.success) {
            setInsufficientCreditsInfo(result.denied);
        }
    };
    
    // Ouvrir le modal quand insufficientCreditsInfo est défini
    useEffect(() => {
        if (insufficientCreditsInfo) {
            const modalElement = document.getElementById('insufficientCreditsModalCompanyAnalysis');
            if (modalElement) {
                // Utiliser setTimeout pour s'assurer que le DOM est prêt
                setTimeout(() => {
                    const bootstrap = (window as any).bootstrap;
                    if (bootstrap && bootstrap.Modal) {
                        const existingModal = bootstrap.Modal.getInstance(modalElement);
                        if (existingModal) {
                            existingModal.show();
                        } else {
                            const newModal = new bootstrap.Modal(modalElement);
                            newModal.show();
                        }
                    }
                }, 100);
            }
        }
    }, [insufficientCreditsInfo]);

    return (
        <>
        <div
            className="left-panel ca-analysis-sidebar-panel d-flex flex-column h-auto"
            data-tutorial="analysis-sidebar"
        >
                {/* Bouton < Home en haut (identique à la page Interview) */}
                <div className="lp-header">
                    <NavLink to={Routes.HOMEVIEW} className="back-btn text-decoration-none">
                        <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {t('words.home')}
                    </NavLink>
                </div>

                <div className="ca-lp-scroll d-flex flex-column" id="navcontainer" data-tutorial="analysis-documents">

                {/* BARRE DE RECHERCHE — même SearchInput que le header / home (HeaderLayout) */}
                <div className="ca-sidebar-search-wrap">
                    <div className="hdr-search-box">
                        <div className="hdr-search-wrap">
                            <SearchInput
                                inputType="text"
                                inputValue={sidebarSearch}
                                placeholderValue={`${t("words.search")}...`}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const v = e.target.value
                                    setSidebarSearch(v)
                                    onSearch?.(v)
                                }}
                                inputContainerClass="w-100"
                                inputStyle={{}}
                                inputClass="new-text-secondary border-0 small"
                                iconClass="fa-solid fa-magnifying-glass"
                                iconName=""
                                formClass="border-0 bg-transparent small w-100 shadow-none hdr-search-form"
                                iconContainer="border-0 bg-transparent"
                                filter={false}
                                fiterOption={[]}
                            />
                        </div>
                    </div>
                </div>

                {/* SECTION 2: Processes — lp-section comme le modèle */}
                <div className="lp-section">
                    <div className="lp-label">{t('features.swimlane.processes') || 'Processus'}</div>
                    <div
                        onClick={onNewDocument}
                        className="ins-new-btn"
                        data-tutorial="new-document-button"
                    >
                        <svg style={{ width: 11, height: 11 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                            <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                        </svg>
                        {t('features.swimlane.addProcess') || 'Ajouter un processus'}
                    </div>
                    <div className="d-flex flex-column" style={{ maxHeight: '120px', overflowY: 'auto', overflowX: 'hidden' }}>
                        {inaccessibleLinks.length > 0 && (
                            <div
                                className="small mb-2 px-2 py-1 rounded-3"
                                style={{ background: '#fff8e6', border: '1px solid #f0d78c', color: '#7a5c00', fontSize: '0.7rem' }}
                                title={inaccessibleLinks.map((l) => l.name).join(', ')}
                            >
                                <i className="fa-solid fa-circle-info me-1" />
                                {t('companyAnalysis.insightsLinkedCollectionInaccessible', {
                                    names: inaccessibleLinks.map((l) => l.name).join(', '),
                                })}
                            </div>
                        )}
                        {(filesToDisplay.length === 0 && (!selectedMaps || selectedMaps.length === 0) && (!selectedCollections || selectedCollections.length === 0)) ? (
                            <div className="ca-sidebar-empty-hint">
                                <span className="d-block">No processes linked.</span>
                                <span className="d-block">Add one to get insights.</span>
                            </div>
                        ) : (
                            <>
                            {/* Fichiers uploadés */}
                            {filesToDisplay.map((file, fileIndex) => (
                                <div 
                                    key={`${file.name}-${fileIndex}`}
                                    className="btn btn-outline-secondary rounded-4 border-0 px-2 diagram-item ca-sidebar-doc-row mb-1"
                                    style={{ minHeight: '40px', padding: '4px 8px' }}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        {/* Lien cliquable pour ouvrir/télécharger le fichier */}
                                        <a 
                                            href={URL.createObjectURL(file)}
                                            {...(
                                                // Ouvrir dans le navigateur pour PDF, images, texte
                                                file.type.includes('pdf') || 
                                                file.type.includes('image') || 
                                                file.type.includes('text')
                                                ? { target: '_blank', rel: 'noopener noreferrer' }
                                                // Télécharger pour Word, Excel, etc.
                                                : { download: file.name }
                                            )}
                                            className="d-flex align-items-center flex-grow-1 text-decoration-none"
                                            style={{ cursor: 'pointer', overflow: 'hidden', maxWidth: '160px' }}
                                            title={
                                                file.type.includes('pdf') || 
                                                file.type.includes('image') || 
                                                file.type.includes('text')
                                                ? `Ouvrir ${file.name}`
                                                : `Télécharger ${file.name}`
                                            }
                                        >
                                            <span>
                                                <i className={`fa-solid ${
                                                    file.type.includes('pdf') ? 'fa-file-pdf' :
                                                    file.type.includes('word') || file.name.endsWith('.docx') ? 'fa-file-word' :
                                                    file.type.includes('excel') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls') ? 'fa-file-excel' :
                                                    file.type.includes('image') ? 'fa-file-image' :
                                                    file.type.includes('text') ? 'fa-file-alt' :
                                                    'fa-file'
                                                } ca-sidebar-doc-icon small`}></i>
                                            </span>
                                            <div className="ca-sidebar-doc-title text-start ps-2" style={{ 
                                                fontSize: '0.8rem', 
                                                whiteSpace: 'nowrap', 
                                                overflow: 'hidden', 
                                                textOverflow: 'ellipsis', 
                                                width: '100%' 
                                            }}>
                                                {file.name}
                                            </div>
                                            {/* Icône pour indiquer qu'on peut ouvrir */}
                                            <i className="fa-solid fa-external-link-alt ca-sidebar-doc-muted small ms-1" style={{ fontSize: '0.7rem' }}></i>
                                        </a>
                                        
                                        {/* Bouton de suppression */}
                                        <button
                                            onClick={() => onRemoveFile && onRemoveFile(fileIndex)}
                                            className="bg-transparent border-0 small"
                                            style={{ cursor: "pointer" }}
                                        >
                                            <i className="fa-solid fa-times ca-sidebar-doc-muted"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                            
                            {/* Maps sélectionnées */}
                            {selectedMaps && selectedMaps.map((map) => (
                                <div 
                                    key={map._id}
                                        className="btn btn-outline-secondary rounded-4 border-0 px-2 diagram-item ca-sidebar-doc-row"
                                        style={{ minHeight: '40px', padding: '4px 8px' }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            {/* Icône et texte à gauche - Cliquable pour ouvrir la cartographie */}
                                            <div 
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (map.process?._id && map._id) {
                                                        handleMapClick(e, map._id, map.process._id);
                                                    }
                                                }}
                                                className="d-flex align-items-center flex-grow-1 text-decoration-none"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <span>
                                                    <i className="fa-solid fa-diagram-project ca-sidebar-doc-icon small"></i>
                                                </span>
                                                
                                                {/* Nom du processus et version */}
                                                <div className="d-flex flex-column align-items-start ps-2 flex-grow-1" style={{ overflow: 'hidden', maxWidth: '130px' }}>
                                                    <div className="ca-sidebar-doc-title text-start" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                                {map.process?.title || 'Processus'}
                                        </div>
                                                    <div className="ca-sidebar-doc-subtitle text-start" style={{ fontSize: "0.65rem", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                            {map.name || 'Version 1'}
                                                    </div>
                                                </div>
                                                {/* Icône pour indiquer qu'on peut ouvrir */}
                                                <i className="fa-solid fa-external-link-alt ca-sidebar-doc-muted small ms-1" style={{ fontSize: '0.7rem' }}></i>
                                            </div>
                                            
                                            {/* Bouton de suppression */}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemoveMap?.(map._id);
                                        }}
                                                className="bg-transparent border-0 small"
                                                style={{ cursor: "pointer" }}
                                            >
                                                <i className="fa-solid fa-times ca-sidebar-doc-muted"></i>
                                    </button>
                                        </div>
                                </div>
                            ))}

                            {selectedCollections && selectedCollections.map((coll) => (
                                <div
                                    key={coll.id}
                                    className="btn btn-outline-secondary rounded-4 border-0 px-2 diagram-item ca-sidebar-doc-row mb-1"
                                    style={{ minHeight: '40px', padding: '4px 8px' }}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div
                                            className="d-flex align-items-center flex-grow-1 text-decoration-none"
                                            style={{ cursor: 'pointer', overflow: 'hidden', maxWidth: '160px' }}
                                            role="button"
                                            tabIndex={0}
                                            title={coll.name}
                                            onClick={() => { void handleCollectionClick(coll.id); }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    void handleCollectionClick(coll.id);
                                                }
                                            }}
                                        >
                                            <i className="fa-solid fa-clipboard-list ca-sidebar-doc-icon small" />
                                            <span className="ps-2 text-truncate small flex-grow-1">{coll.name}</span>
                                            <i className="fa-solid fa-external-link-alt ca-sidebar-doc-muted small ms-1" style={{ fontSize: '0.7rem' }} />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onRemoveCollection && onRemoveCollection(coll.id);
                                            }}
                                            className="bg-transparent border-0 small"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <i className="fa-solid fa-times ca-sidebar-doc-muted" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            </>
                        )}
                        </div>
                </div>

                <hr className="ndiv" />

                {/* SECTION 4: Recent insights — style ChatGPT/Claude */}
                <div className="ca-insights-section">
                    {/* En-tête avec toggle Show/Hide */}
                    <div className="ca-insights-header">
                        <span className="lp-label mb-0">{t('features.swimlane.recentInsights') || 'Recent Insights'}</span>
                        <button
                            className="ca-insights-toggle"
                            onClick={() => setInsightsVisible(v => !v)}
                            aria-label={insightsVisible ? 'Hide insights' : 'Show insights'}
                            title={insightsVisible ? 'Hide' : 'Show'}
                        >
                            {insightsVisible ? 'Hide' : 'Show'}
                        </button>
                    </div>

                    {insightsVisible && (
                        <div className="ca-insights-list">
                            {isLoadingAnalyses ? (
                                <Spiner className="spinner-border-sm" />
                            ) : (() => {
                                const filtered = sidebarSearch.trim()
                                    ? analyses.filter((a: any) =>
                                          (a.name || '').toLowerCase().includes(sidebarSearch.toLowerCase())
                                      )
                                    : analyses

                                if (filtered.length === 0) {
                                    return (
                                        <div className="ca-sidebar-empty-hint">
                                            {sidebarSearch
                                                ? (t('words.noResults') || 'No results')
                                                : (t('views.processInterview.noAnalyses') || 'No insights yet')}
                                        </div>
                                    )
                                }

                                return filtered.map((analysis: any) => {
                                    const isActive = activeAnalysisId === analysis.analysis_id
                                    return (
                                        <div
                                            key={analysis.analysis_id}
                                            className={`ca-insight-item ${isActive ? 'ca-insight-item--active' : ''}`}
                                        >
                                            <div
                                                className="ca-insight-item-body"
                                                onClick={() => {
                                                    if (analysisState === 'loading') return
                                                    setActiveAnalysisId(analysis.analysis_id)
                                                    onSelectAnalysis && onSelectAnalysis(analysis.analysis_id, analysis.name)
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault()
                                                        if (analysisState === 'loading') return
                                                        setActiveAnalysisId(analysis.analysis_id)
                                                        onSelectAnalysis && onSelectAnalysis(analysis.analysis_id, analysis.name)
                                                    }
                                                }}
                                            >
                                                <svg className="ca-insight-icon" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
                                                    <path d="M2 12L6 8L9 11L13 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                <span className="ca-insight-name">
                                                    {analysis.name || 'Insight'}
                                                </span>
                                            </div>

                                            {isAnalysisOwner(analysis, users.user._id) && (
                                                <div className="ca-insight-actions">
                                                    <Dropdown
                                                        dropName=""
                                                        dropdownType="btn-group dropend"
                                                        iconClassName="fa fa-ellipsis-vertical small dropdown_icon"
                                                        buttonDropClassName="bg-transparent border-0 small"
                                                        styleButton={{}}
                                                        deleteProcess={() => deleteAnalysis(analysis.analysis_id, 'deleteAnalyse', "")}
                                                        action1={t('words.delete')}
                                                        theRouteOfAction4=""
                                                        theRouteOfAction5=""
                                                        routeFaq=""
                                                        rename={t('words.rename')}
                                                        share={t('words.share')}
                                                        renameAction={() => renameAnalysis(analysis.analysis_id, analysis.name || '', "renameAnalyse")}
                                                        shareAction={() => shareAnalysis(analysis.analysis_id)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )
                                })
                            })()}
                        </div>
                    )}
                </div>

                </div>

                {/* Profil utilisateur en bas (identique à la page Interview) */}
                <div className="">
                    <SidebarUserProfileSection showChangeCompanyInMenu />
                </div>

        </div>

        {/* Modal Renommage - À DÉPLACER dans CompanyAnalysis.tsx */}
            {modalAction === 'rename' && (
                <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                            <div className="modal-header border-secondary">
                                <h5 className="modal-title">{t('words.rename') || 'Renommer'}</h5>
                                <button 
                                    type="button" 
                                    className={modalCloseClass}
                                    onClick={() => {
                                        setModalAction(null)
                                        setRenameError('')
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">{t('features.swimlane.analysisName') || 'Nom de l\'analyse'}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={selectedAnalysisName}
                                        onChange={(e) => {
                                            setSelectedAnalysisName(e.target.value)
                                            setRenameError('')
                                        }}
                                        style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}
                                        autoFocus
                                        disabled={isRenaming}
                                    />
                                    {renameError && (
                                        <div className="mt-2 small" style={{ color: '#FF6B6B' }}>
                                            <i className="fa-solid fa-exclamation-circle me-1"></i>
                                            {renameError}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer border-secondary">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setModalAction(null)
                                        setRenameError('')
                                    }}
                                    disabled={isRenaming}
                                >
                                    {t('words.cancel') || 'Annuler'}
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={() => confirmRenameAnalysis(selectedAnalysisName)}
                                    disabled={isRenaming || !selectedAnalysisName.trim()}
                                >
                                    {isRenaming
                                        ? (t('words.saving') || 'Sauvegarde...')
                                        : (t('words.save') || 'Sauvegarder')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Partage */}
            {modalAction === 'share' && (
                <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                            <div className="modal-header border-secondary">
                                <h5 className="modal-title">{t('words.share') || 'Partager'}</h5>
                                <button 
                                    type="button" 
                                    className={modalCloseClass}
                                    onClick={() => setModalAction(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">{t('words.shareLink') || 'Lien de partage'}</label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={`${window.location.origin}/company/analysis/${selectedAnalysisId}`}
                                            readOnly
                                            style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-subtle)' }}
                                        />
                                        <button 
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                navigator.clipboard.writeText(`${window.location.origin}/company/analysis/${selectedAnalysisId}`)
                                            }}
                                        >
                                            <i className="fa-solid fa-copy"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer border-secondary">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => setModalAction(null)}
                                >
                                    {t('words.close') || 'Fermer'}
                                </button>
                            </div>
                    </div>
                </div>
            </div>
            )}

            {/* Modal Crédits Insuffisants */}
            {insufficientCreditsInfo && (
                <>
                    <span ref={insufficientCreditsModalRef}></span>
                    <InsufficientCreditsModal
                        id="insufficientCreditsModalCompanyAnalysis"
                        creditsRequired={insufficientCreditsInfo.creditsRequired}
                        creditsAvailable={insufficientCreditsInfo.creditsAvailable}
                        deficit={insufficientCreditsInfo.deficit}
                        plan={insufficientCreditsInfo.plan}
                        onClose={() => {
                            setInsufficientCreditsInfo(null);
                        }}
                        isOwner={isOwner}
                        adminEmail={ownerEmail}
                    />
                </>
            )}
        </>
    )
}

export default CompanyAnalysisSidebar


